import { useFormatter, useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { IntervalType } from 'react-markdown-typer';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { CLAW_LOGO_IMAGE_URL, THINKING_GIF } from '@/app/constants';
import { useCopy } from '@/app/hooks';
import { formatDateTime } from '@/app/utils';
import type { ChatMessageItem } from '../../../types';
import { ClawMarkdown } from '../../ClawMarkdown';
import { MessageAttachmentList } from './MessageAttachmentList';
import { ThinkingContent } from './ThinkingCon';
import { ToolContent } from './ToolCon';

interface AssistantMessageProps {
  message: ChatMessageItem;
  isActiveConversationTool?: boolean;
  /** 伪打字未完成时隐藏复制/时间，避免终态信息过早出现。 */
  isTypingPlayback?: boolean;
  /** 分享页 replay 模式：组件内用 RAF 播放内容，不依赖 MarkdownTyper。 */
  forceTyping?: boolean;
  /** 主聊天页 MarkdownTyper 的打字节奏；replay 模式不使用。 */
  typingInterval?: IntervalType;
  /** replay 模式每帧追加字符数。 */
  replayCharsPerFrame?: number;
  clawId?: string;
  sessionId?: string;
  onPreviewFile?: (filePath: string) => void;
  /**
   * 关闭正文内 `media/` 链接的点击预览（chip 降级为纯展示）。
   * 用于分享页等只读场景——注意此开关不影响底部产物附件列表的预览。
   */
  disableInlineFilePreview?: boolean;
  onQuoteFile?: (filePath: string) => void;
  /** 内容真正播放完成时上报。 */
  onTypingEnd?: (messageId: string) => void;
}

const DEFAULT_REPLAY_CHARS_PER_FRAME = 6;

export const AssistantMessage = memo(
  ({
    message,
    isActiveConversationTool = false,
    isTypingPlayback = false,
    forceTyping = false,
    typingInterval,
    replayCharsPerFrame = DEFAULT_REPLAY_CHARS_PER_FRAME,
    clawId,
    sessionId,
    onPreviewFile,
    disableInlineFilePreview = false,
    onQuoteFile,
    onTypingEnd,
  }: AssistantMessageProps) => {
    const t = useTranslations();
    const format = useFormatter();
    const { copy } = useCopy();

    const { content, createdAt = '', thinking, thinkingLoading, tool_calls = [], fileList = [] } = message;

    // content 可能一次性落入，但只要本条消息曾是流式目标，就继续交给 MarkdownTyper 播完。
    // 用 state 单调记录，避免渲染期写 ref 带来的并发渲染差异。
    const [hasBeenActive, setHasBeenActive] = useState(false);
    if (isActiveConversationTool && !hasBeenActive) {
      setHasBeenActive(true);
    }
    const useTyper = isActiveConversationTool || hasBeenActive;

    // streaming 期间每个 chunk 边界都可能触发 onEnd；只有 streaming 结束后的下一次 onEnd 才算终态。
    const wasActiveRef = useRef(isActiveConversationTool);
    const awaitingFinalEndRef = useRef(false);
    const reportedRef = useRef(false);
    const messageIdRef = useRef(message.id);
    const onTypingEndRef = useRef(onTypingEnd);

    useEffect(() => {
      messageIdRef.current = message.id;
      onTypingEndRef.current = onTypingEnd;
    });

    useEffect(() => {
      if (wasActiveRef.current && !isActiveConversationTool) {
        awaitingFinalEndRef.current = true;
      }
      wasActiveRef.current = isActiveConversationTool;
    }, [isActiveConversationTool]);

    const handleTyperEnd = useCallback(() => {
      if (!awaitingFinalEndRef.current) return;
      if (reportedRef.current) return;
      awaitingFinalEndRef.current = false;
      reportedRef.current = true;
      onTypingEndRef.current?.(messageIdRef.current);
    }, []);

    // replayContent 为 null 表示展示完整内容；非 null 表示正在按 RAF 播放。
    // 懒初始化为空字符串，避免 replay 首帧闪出完整内容。
    const [replayContent, setReplayContent] = useState<string | null>(() => (forceTyping ? '' : null));
    const replayCharsPerFrameRef = useRef(replayCharsPerFrame);
    replayCharsPerFrameRef.current = replayCharsPerFrame;

    useEffect(() => {
      if (!forceTyping) {
        setReplayContent(null);
        return;
      }
      if (reportedRef.current) {
        setReplayContent(null);
        return;
      }
      const target = content ?? '';
      if (target.length === 0) {
        reportedRef.current = true;
        setReplayContent(null);
        onTypingEndRef.current?.(messageIdRef.current);
        return;
      }
      let cursor = 0;
      let rafId: number | null = null;
      setReplayContent('');
      const step = () => {
        cursor = Math.min(cursor + Math.max(1, replayCharsPerFrameRef.current), target.length);
        setReplayContent(target.slice(0, cursor));
        if (cursor < target.length) {
          rafId = requestAnimationFrame(step);
          return;
        }
        rafId = null;
        reportedRef.current = true;
        // 播放结束后回到完整 content 路径，后续切换 replay 目标时保持终态展示。
        setReplayContent(null);
        onTypingEndRef.current?.(messageIdRef.current);
      };
      rafId = requestAnimationFrame(step);
      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }, [forceTyping, content]);

    const handleCopy = () => {
      copy(content);
    };

    const displayContent = forceTyping && replayContent !== null ? replayContent : content;
    const shareMessageId = message.shareMessageIds?.[message.shareMessageIds.length - 1] ?? message.serverMessageId;

    return (
      <div className="flex items-start gap-3 pt-10">
        <div className="size-8 shrink-0 overflow-hidden rounded-full bg-white sm:hidden">
          <img src={CLAW_LOGO_IMAGE_URL} alt="assistant avatar" className="size-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          {(thinking || tool_calls.length > 0) && (
            <div className="mb-2 flex flex-col flex-wrap gap-2">
              {thinking && <ThinkingContent thinking={thinking} />}
              {tool_calls.length > 0 && (
                <ToolContent tool_calls={tool_calls} isActiveConversationTool={isActiveConversationTool} />
              )}
            </div>
          )}

          {thinkingLoading && (
            <div className="mb-2 flex items-center gap-1">
              <img src={THINKING_GIF} alt="thinking" className="size-5" />
              <div className="animate-shimmer-flow bg-[length:200%_100%] bg-[linear-gradient(90deg,rgba(255,255,255,0.3)_0%,#FFF_20%,rgba(255,255,255,0.3)_40%,rgba(255,255,255,0.3)_60%,#FFF_80%,rgba(255,255,255,0.3)_100%)] light:bg-[linear-gradient(90deg,rgba(0,0,0,0.3)_0%,#FFF_20%,rgba(0,0,0,0.3)_40%,rgba(0,0,0,0.3)_60%,#FFF_80%,rgba(0,0,0,0.3)_100%)] bg-clip-text text-sm text-transparent">
                {t('claw.chat.thinking.loading')}
              </div>
            </div>
          )}

          <ClawMarkdown
            content={displayContent}
            normalizeEscapedNewline={true}
            typing={useTyper}
            typingInterval={typingInterval}
            onTypingEnd={useTyper ? handleTyperEnd : undefined}
            onPreviewFile={disableInlineFilePreview ? undefined : onPreviewFile}
          />

          {fileList && fileList.length > 0 && (
            <div className="mt-3" data-anchor-id={message.id} data-anchor-role="assistant">
              <MessageAttachmentList
                files={fileList}
                clawId={clawId}
                shareSessionId={sessionId}
                shareMessageId={shareMessageId}
                onPreviewFile={onPreviewFile}
                onQuoteFile={onQuoteFile}
              />
            </div>
          )}

          {(createdAt || content) && !isActiveConversationTool && !isTypingPlayback && (
            <div className="mt-3 flex items-center gap-2 text-system-white36">
              {content && <BaseLineCopy className="size-4 cursor-pointer" onClick={handleCopy} />}
              <div className="h-3 w-px bg-system-white16" />
              <div className="text-xs">{formatDateTime(createdAt, format)}</div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

AssistantMessage.displayName = 'AssistantMessage';
